import type { BuilderWeatherSource } from '../schemas/builder';

/** Provider-neutral metric values. Display units and language belong to each element. */
export interface BuilderWeatherSnapshot {
  observedAtMs: number;
  temperatureC?: number;
  feelsLikeC?: number;
  humidityPercent?: number;
  conditionCode?: number;
  windSpeedKmh?: number;
}

type WeatherCondition = 'clear' | 'mainlyClear' | 'partlyCloudy' | 'overcast' | 'fog'
  | 'drizzle' | 'freezingDrizzle' | 'rain' | 'freezingRain' | 'snow' | 'snowGrains'
  | 'rainShowers' | 'snowShowers' | 'thunderstorm' | 'thunderstormHail';

const weatherConditions: Readonly<Record<number, WeatherCondition>> = {
  0: 'clear', 1: 'mainlyClear', 2: 'partlyCloudy', 3: 'overcast',
  45: 'fog', 48: 'fog', 51: 'drizzle', 53: 'drizzle', 55: 'drizzle',
  56: 'freezingDrizzle', 57: 'freezingDrizzle', 61: 'rain', 63: 'rain', 65: 'rain',
  66: 'freezingRain', 67: 'freezingRain', 71: 'snow', 73: 'snow', 75: 'snow',
  77: 'snowGrains', 80: 'rainShowers', 81: 'rainShowers', 82: 'rainShowers',
  85: 'snowShowers', 86: 'snowShowers', 95: 'thunderstorm',
  96: 'thunderstormHail', 99: 'thunderstormHail',
};

const conditionTranslations = {
  en: {
    clear: 'Clear', mainlyClear: 'Mainly clear', partlyCloudy: 'Partly cloudy', overcast: 'Overcast', fog: 'Fog',
    drizzle: 'Drizzle', freezingDrizzle: 'Freezing drizzle', rain: 'Rain', freezingRain: 'Freezing rain',
    snow: 'Snow', snowGrains: 'Snow grains', rainShowers: 'Rain showers', snowShowers: 'Snow showers',
    thunderstorm: 'Thunderstorm', thunderstormHail: 'Thunderstorm with hail',
  },
  fr: {
    clear: 'Ciel dégagé', mainlyClear: 'Généralement dégagé', partlyCloudy: 'Partiellement nuageux',
    overcast: 'Ciel couvert', fog: 'Brouillard', drizzle: 'Bruine', freezingDrizzle: 'Bruine verglaçante',
    rain: 'Pluie', freezingRain: 'Pluie verglaçante', snow: 'Neige', snowGrains: 'Neige en grains',
    rainShowers: 'Averses de pluie', snowShowers: 'Averses de neige', thunderstorm: 'Orage',
    thunderstormHail: 'Orage avec grêle',
  },
  ar: {
    clear: 'صافٍ', mainlyClear: 'صافٍ غالبًا', partlyCloudy: 'غائم جزئيًا', overcast: 'غائم', fog: 'ضباب',
    drizzle: 'رذاذ', freezingDrizzle: 'رذاذ متجمد', rain: 'مطر', freezingRain: 'مطر متجمد',
    snow: 'ثلج', snowGrains: 'حبيبات ثلجية', rainShowers: 'زخات مطر', snowShowers: 'زخات ثلج',
    thunderstorm: 'عاصفة رعدية', thunderstormHail: 'عاصفة رعدية مع برد',
  },
} satisfies Record<string, Record<WeatherCondition, string>>;

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : undefined;
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Expects Open-Meteo current values requested in Celsius, km/h and Unix seconds. */
export function normalizeOpenMeteoWeather(response: unknown): BuilderWeatherSnapshot | null {
  const current = record(record(response)?.['current']);
  if (!current) return null;
  const time = finiteNumber(current['time']);
  if (time === undefined || !Number.isFinite(new Date(time * 1_000).getTime())) return null;

  const snapshot: BuilderWeatherSnapshot = { observedAtMs: time * 1_000 };
  const temperature = finiteNumber(current['temperature_2m']);
  const feelsLike = finiteNumber(current['apparent_temperature']);
  const humidity = finiteNumber(current['relative_humidity_2m']);
  const windSpeed = finiteNumber(current['wind_speed_10m']);
  const code = finiteNumber(current['weather_code']);
  if (temperature !== undefined) snapshot.temperatureC = temperature;
  if (feelsLike !== undefined) snapshot.feelsLikeC = feelsLike;
  if (humidity !== undefined && humidity >= 0 && humidity <= 100) snapshot.humidityPercent = humidity;
  if (windSpeed !== undefined && windSpeed >= 0) snapshot.windSpeedKmh = windSpeed;
  if (code !== undefined && Object.hasOwn(weatherConditions, code)) snapshot.conditionCode = code;
  return Object.keys(snapshot).length > 1 ? snapshot : null;
}

function formatNumber(value: number | undefined, unit: string, locale?: string): string | undefined {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const options: Intl.NumberFormatOptions = { maximumFractionDigits: 1 };
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(locale, options).format(value === 0 ? 0 : value);
  } catch {
    formatted = new Intl.NumberFormat('en-CA', options).format(value === 0 ? 0 : value);
  }
  return `${formatted} ${unit}`;
}

export function getBuilderWeatherText(
  source: BuilderWeatherSource, snapshot: BuilderWeatherSnapshot | undefined, locale?: string,
): string | undefined {
  if (!snapshot) return undefined;
  switch (source.type) {
    case 'weather.temperature': case 'weather.feelsLike': {
      const celsius = source.type === 'weather.temperature' ? snapshot.temperatureC : snapshot.feelsLikeC;
      return source.unit === 'fahrenheit'
        ? formatNumber(celsius === undefined ? undefined : celsius * 9 / 5 + 32, '°F', locale)
        : formatNumber(celsius, '°C', locale);
    }
    case 'weather.humidity':
      return snapshot.humidityPercent !== undefined && snapshot.humidityPercent >= 0 && snapshot.humidityPercent <= 100
        ? formatNumber(snapshot.humidityPercent, '%', locale) : undefined;
    case 'weather.wind.speed': {
      const kmh = snapshot.windSpeedKmh;
      if (kmh === undefined || kmh < 0) return undefined;
      return source.unit === 'mph' ? formatNumber(kmh / 1.609344, 'mph', locale) : formatNumber(kmh, 'km/h', locale);
    }
    case 'weather.condition': {
      const code = snapshot.conditionCode;
      if (code === undefined || !Object.hasOwn(weatherConditions, code)) return undefined;
      const language = locale?.toLowerCase().split('-')[0];
      const translations = language === 'fr' ? conditionTranslations.fr
        : language === 'ar' ? conditionTranslations.ar : conditionTranslations.en;
      return translations[weatherConditions[code]];
    }
  }
}
